import { Component } from '@opten/gin-tonic/helpers/compiler';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

@Component({ className: 'js-keyvisual' })
export class KeyVisualComponent {
	private slides: HTMLElement[] = [];
	private mode: 'timed' | 'scroll' = 'timed';
	private tl: gsap.core.Timeline | null = null;
	private active = false; // intersection state for scroll mode
	private sectionTop = 0;
	private sectionHeight = 0;
	private viewportH = window.innerHeight;
	private resizeRaf: number | null = null;
	private scrollRaf: number | null = null;
	private element: HTMLElement;
	private scroller: HTMLElement | null = null;

	constructor(element: HTMLElement) {
		// Normalize: always use the root module container as the element
		const root =
			(element.closest("[data-component='keyvisual']") as HTMLElement) ||
			element;
		// Prevent duplicate initialization (e.g., decorator on .js-keyvisual and manual init on container)
		if ((root as any).dataset && (root as any).dataset.kvInitialized) {
			// Already initialized on the root, abort this instance
			this.element = root;
			return;
		}
		if ((root as any).dataset) (root as any).dataset.kvInitialized = '1';
		this.element = root;
		this.scroller =
			(this.element.querySelector('.c-keyvisual__scroller') as HTMLElement) ||
			null;

		// Wire scroll-hint interactions (click + keyboard)
		this.initScrollHint();

		this.slides = Array.from(
			this.element.querySelectorAll('[data-kv-slide]')
		) as HTMLElement[];
		// Theme detection: prefer class-based modifiers, fallback to legacy data-theme
		const isTitleOnly =
			this.element.classList.contains('c-keyvisual--titleonly') ||
			this.element.getAttribute('data-theme') === '--titleonly';
		if (isTitleOnly) this.mode = 'scroll';
		this.init();
	}

	private init() {
		if (!this.slides.length) return;
		if (this.mode === 'timed') {
			this.initTimed();
		} else {
			this.initScroll();
		}
	}

	private initScrollHint() {
		const hint = this.element.querySelector<HTMLElement>(
			'.js-keyvisua-scroll-hint'
		);
		if (!hint) return;
		// Improve a11y and interactivity
		hint.setAttribute('role', 'button');
		if (!hint.hasAttribute('tabindex')) hint.tabIndex = 0;
		if (!hint.getAttribute('aria-label'))
			hint.setAttribute('aria-label', 'Scroll down');

		const onActivate = (e?: Event) => {
			if (e) e.preventDefault();
			this.scrollBelow();
		};
		hint.addEventListener('click', onActivate);
		hint.addEventListener('keydown', (ev: KeyboardEvent) => {
			if (ev.key === 'Enter' || ev.key === ' ' || ev.code === 'Space') {
				ev.preventDefault();
				onActivate(ev);
			}
		});
	}

	private getHeaderHeightPx(): number {
		const v = getComputedStyle(document.documentElement).getPropertyValue(
			'--header-height'
		);
		const n = parseFloat(v);
		return isNaN(n) ? 0 : n;
	}

	private scrollBelow() {
		// Scroll smoothly to just below the keyvisual module, accounting for sticky header
		const reduce =
			window.matchMedia &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		const rect = this.element.getBoundingClientRect();
		const header = this.getHeaderHeightPx();
		const target = Math.max(
			0,
			Math.round(rect.bottom + window.scrollY - header)
		);
		if (!reduce) {
			window.scrollTo({ top: target, behavior: 'smooth' });
		} else {
			window.scrollTo(0, target);
		}
	}

	// ---------------- Timed crossfade ----------------
	private initTimed() {
		// Safety: do not run timed mode when theme is --titleonly
		if (this.mode === 'scroll') {
			return;
		}
		// Ensure first slide visible
		gsap.set(this.slides, { opacity: 0 });
		gsap.set(this.slides[0], { opacity: 1 });
		this.slides[0].classList.add('c-keyvisual__slide--active');
		if (this.slides.length > 1) {
			// Faster fade & slightly tighter cycle per request
			const display = 5; // seconds each fully visible
			const fade = 0.3; // seconds fade in/out
			const total = display + fade; // simplified cadence
			const tl = gsap.timeline({
				repeat: -1,
				defaults: { ease: 'power2.inOut' },
			});
			this.slides.forEach((slide, i) => {
				const start = i * total;
				tl.to(
					slide,
					{ opacity: 1, duration: fade, onStart: () => this.setActive(i) },
					start
				);
				tl.to(
					slide,
					{ opacity: 0, duration: fade, onComplete: () => this.clearActive(i) },
					start + display
				);
			});
			this.tl = tl;
			// this.attachInteractionPause();
		}
	}

	private setActive(index: number) {
		this.slides.forEach((s, i) =>
			s.setAttribute('aria-hidden', i === index ? 'false' : 'true')
		);
		this.slides.forEach((s, i) =>
			s.classList.toggle('c-keyvisual__slide--active', i === index)
		);
	}
	private clearActive(index: number) {
		// nothing special; timeline sets next one
	}

	private attachInteractionPause() {
		if (!this.tl) return;
		const interactive = this.element.querySelectorAll<HTMLElement>(
			'.c-keyvisual__content, .c-keyvisual__btn, .c-keyvisual__textlink'
		);
		interactive.forEach((el) => {
			el.addEventListener('mouseenter', () => this.tl?.pause());
			el.addEventListener('mouseleave', () => this.tl?.play());
			el.addEventListener('focusin', () => this.tl?.pause());
			el.addEventListener('focusout', () => this.tl?.play());
		});
	}

	// ---------------- Scroll driven crossfade ----------------
	private initScroll() {
		// Scrap manual scroll math: drive with GSAP + ScrollTrigger.
		const n = this.slides.length;
		if (n === 0) return;
		// Initial visual state: first slide visible, others staged.
		this.slides.forEach((s, i) => {
			gsap.set(s, { opacity: i === 0 ? 1 : 0, y: 0, scale: 1 });
			s.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
			s.classList.toggle('c-keyvisual__slide--active', i === 0);
		});

		// Timing model
		const preHold = 0.25; // delay before the very first transition begins (fraction of total scroll)
		const segmentNorm = n > 1 ? 1 / (n - 1) : 1; // per-segment fraction in normalized space [0..1]
		const segmentScaled = n > 1 ? (1 - preHold) / (n - 1) : 1; // effective segment length when accounting for pre-hold
		const windowFrac = 0.2; // animate only in first 20% of each segment
		const fadeDur = segmentScaled * windowFrac;

		// Build a scrubbed timeline normalized to progress [0..1]
		const tl = gsap.timeline({
			defaults: { ease: 'none' },
			scrollTrigger: {
				trigger: this.scroller || this.element,
				start: () => `top 120%`,
				end: () => `bottom 120%`,
				scrub: true,
				invalidateOnRefresh: true,
				onUpdate: (self) => {
					// Update aria and active classes based on segment and local progress
					const p = self.progress;
					if (n === 1) return;
					if (p < preHold) {
						this.slides.forEach((s, i) => {
							s.classList.toggle('c-keyvisual__slide--active', i === 0);
							s.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
						});
						return;
					}
					const p2 = Math.min(1, Math.max(0, (p - preHold) / (1 - preHold)));
					const idx = Math.max(
						0,
						Math.min(n - 2, Math.floor(p2 / segmentNorm))
					);
					const segStart = idx * segmentNorm;
					const local = (p2 - segStart) / (segmentNorm || 1);
					const inWindow = local <= windowFrac + 1e-6;
					this.slides.forEach((s, i) => {
						const isActive = inWindow
							? i === idx || i === idx + 1
							: i === idx + 1;
						s.classList.toggle('c-keyvisual__slide--active', isActive);
						s.setAttribute('aria-hidden', isActive ? 'false' : 'true');
					});
				},
			},
		});

		// For each segment i (slide i -> slide i+1), crossfade (and subtle scale on incoming) only in first 20% of segment
		for (let i = 0; i < n - 1; i++) {
			const at = preHold + i * segmentScaled;
			// Outgoing current slide (no vertical shift to avoid perceived scroll-up)
			tl.to(
				this.slides[i],
				{ opacity: 0, duration: fadeDur, ease: 'power1.out' },
				at
			);
			// Incoming next slide with subtle scale-in
			tl.fromTo(
				this.slides[i + 1],
				{ opacity: 0, scale: 1.0 },
				{ opacity: 1, scale: 1, duration: fadeDur, ease: 'power1.out' },
				at
			);
		}

		this.tl = tl;
	}

	private observe() {
		// No-op with ScrollTrigger-based implementation
	}

	private onResize = () => {
		// No-op: ScrollTrigger handles refresh on resize via invalidateOnRefresh
	};

	private computeLayout() {
		// Deprecated by ScrollTrigger implementation
	}

	private onScroll = () => {
		// No-op with ScrollTrigger
	};

	private requestScrollUpdate() {
		// No-op with ScrollTrigger
	}

	private updateScroll() {
		// No-op with ScrollTrigger
	}

	public destroy() {
		if (this.tl) {
			(this.tl.scrollTrigger as any)?.kill?.();
			this.tl.kill();
		}
	}
}

// Auto-init fallback
(function init() {
	const nodes = document.querySelectorAll<HTMLElement>(
		'[data-component="keyvisual"]'
	);
	nodes.forEach((n) => new KeyVisualComponent(n));
})();

@Component({ className: 'js-keyvisual-trigger-addition' })
export class KeyVisualAdditionComponent {
	private contentAddition: HTMLElement | null = null;
	private imageAddition: HTMLElement | null = null;
	private additionElement: HTMLElement | null = null;
	constructor(additionElement: HTMLElement) {
		this.additionElement = additionElement;
		this.imageAddition = additionElement.querySelector(
			'.js-keyvisual-image-addition'
		);

		this.contentAddition = additionElement.querySelector(
			'.js-keyvisual-content-addition'
		);
		this.checkSize();
		window.addEventListener('resize', () => this.checkSize());
	}

	checkSize() {
		var additionWidth = this.contentAddition?.clientWidth;
		this.imageAddition?.style.setProperty(
			'--keyvisual-addition-width',
			`${additionWidth}px`
		);
	}
}
